import React from 'react';
import styled, { keyframes } from 'styled-components';

const barSlide = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
`;

const dotPulse = keyframes`
  0%,
  80%,
  100% {
    transform: scale(0.65);
    opacity: 0.45;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const Shell = styled.div`
  min-height: 100dvh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.theme.background};
  position: relative;
`;

const TopTrack = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: ${(p) => p.theme.border};
  overflow: hidden;
  z-index: 10000;
`;

const TopFill = styled.div`
  height: 100%;
  width: 35%;
  max-width: 240px;
  background: ${(p) => p.theme.primary};
  box-shadow: 0 0 12px ${(p) => p.theme.primaryGlow};
  animation: ${barSlide} 1.1s ease-in-out infinite;
`;

const CenterBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 1.5rem;
  max-width: 22rem;
`;

const DotsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 1rem;
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.theme.primary};
  animation: ${dotPulse} 1s ease-in-out infinite;
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const Caption = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.45;
  color: ${(p) => p.theme.mutedForeground};
  text-align: center;
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export type RouteGateFallbackVariant = 'chunk' | 'auth' | 'permissions' | 'admin';

const defaultCaption: Record<RouteGateFallbackVariant, string | undefined> = {
  chunk: undefined,
  auth: 'Verifying your session',
  permissions: 'Checking access',
  admin: 'Verifying admin access',
};

export interface RouteGateFallbackProps {
  /** Controls default helper text; omit for code-split / neutral load */
  variant?: RouteGateFallbackVariant;
  /** Overrides variant caption; pass empty string to hide */
  message?: string;
}

/**
 * Lightweight loading shell for route gates and Suspense (auth, permissions, lazy chunks).
 * When a caption is shown, animated dots appear above the text (production-style wait state).
 */
const RouteGateFallback: React.FC<RouteGateFallbackProps> = ({
  variant = 'chunk',
  message,
}) => {
  const resolved =
    message !== undefined ? (message === '' ? undefined : message) : defaultCaption[variant];

  return (
    <Shell aria-busy="true" aria-live="polite">
      <SrOnly>Loading</SrOnly>
      <TopTrack aria-hidden>
        <TopFill />
      </TopTrack>
      {resolved ? (
        <CenterBlock>
          <DotsRow aria-hidden>
            <Dot />
            <Dot />
            <Dot />
          </DotsRow>
          <Caption>{resolved}</Caption>
        </CenterBlock>
      ) : null}
    </Shell>
  );
};

export default RouteGateFallback;
